import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Input,
  Radio,
  notification,
  TimePicker,
  Form,
  Spin,
  Alert,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import axiosInstance from "../../../axios";
import {
  StudentPersonalDetailsType,
  TimingAndScheduleStateType,
} from "../types";
import dayjs from "dayjs";
import {
  DAYS_PER_WEEK_MASTER,
  timeFromString,
  toTimeString,
} from "../../constants";

import {
  ActionForNotification,
  NotificationType,
  popupTitleAndMessage,
} from "../../utils/notificationPopup";
import { omit } from "lodash";

type EditTimeAndScheduleProps = {
  data: StudentPersonalDetailsType;
  tutionTimingAndState?: TimingAndScheduleStateType;
  setTimingAndScheduleState: React.Dispatch<
    React.SetStateAction<TimingAndScheduleStateType | undefined>
  >;
  afterClose: () => Promise<void>;
};
// TODO Add form and formhandling for reset

type FormState = Partial<TimingAndScheduleStateType>;

function EditTimeAndSchedule(props: EditTimeAndScheduleProps) {
  const [spinning, setSpinning] = useState(false);
  const [NotificationInstanceApi, NotificationInstanceContextHolder] =
    notification.useNotification();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaveError, setIsSaveError] = useState(false);
  const { setTimingAndScheduleState, tutionTimingAndState } = props;
  const [classesPerWeek, setClassesPerWeek] = useState(
    tutionTimingAndState?.how_many_days_in_a_week ?? 0
  );
  const [startTime, setStartTime] = useState<dayjs.Dayjs | undefined>(
    tutionTimingAndState?.tuition_start_time
  );
  const [endTime, setEndTime] = useState<dayjs.Dayjs | undefined>(
    tutionTimingAndState?.tuition_end_time
  );
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(
    tutionTimingAndState?.tuition_hours as number
  );
  const [addNote, setAddNote] = useState<string>(
    tutionTimingAndState?.note ?? ""
  );
  
  const [newTimingAndScheduleState, setNewTimingAndScheduleState] = useState<
    FormState | undefined
  >(tutionTimingAndState);

  const [form] = Form.useForm();

  const showModal = async () => {
    setLoading(false);
    setIsModalVisible(true);

    form.setFieldsValue({
      how_many_days_in_a_week:
        tutionTimingAndState?.how_many_days_in_a_week ?? 0,
      tuition_start_time: tutionTimingAndState?.tuition_start_time,
      tuition_end_time: tutionTimingAndState?.tuition_end_time,
      tuition_hours: tutionTimingAndState?.tuition_hours,
      note: tutionTimingAndState?.note ?? "",
    });

    setClassesPerWeek(tutionTimingAndState?.how_many_days_in_a_week ?? 0);
    setStartTime(tutionTimingAndState?.tuition_start_time);
    setEndTime(tutionTimingAndState?.tuition_end_time);
    setHoursPerWeek(tutionTimingAndState?.tuition_hours as number);
    setAddNote(tutionTimingAndState?.note ?? "");
    setNewTimingAndScheduleState(tutionTimingAndState);
  };

  const handleClose = () => {
    setIsModalVisible(false);
  };

  function handleValuesChange(
    _changedValues: Record<string, string | undefined>,
    allValues: FormState
  ): void {
    console.log("All values is always", allValues);
    setNewTimingAndScheduleState(allValues);
  }

  async function storeDealTimingSchedule() {
    try {
      const response = await axiosInstance.post(
        `/v1/cre/student/timing-schedule`,
        {
          dealings_id: props.data.id,
          tuition_start_time: toTimeString(
            newTimingAndScheduleState?.tuition_start_time ?? null
          ),
          tuition_end_time: toTimeString(
            newTimingAndScheduleState?.tuition_end_time ?? null
          ),
          tuition_hours: newTimingAndScheduleState?.tuition_hours,
          how_many_days_in_a_week:
            newTimingAndScheduleState?.how_many_days_in_a_week,
          note: addNote,
        }
      );
      form.resetFields();
      if (response.data.success) {
        setIsModalVisible(false);
        const timingState = omit(response.data?.data, ["id"]);
        setTimingAndScheduleState({
          ...timingState,
          ...{
            tuition_start_time: timeFromString(timingState.tuition_start_time),
          },
          ...{ tuition_end_time: timeFromString(timingState.tuition_end_time) },
        } as TimingAndScheduleStateType);
        openNotificationWithIcon("success", "UPDATE_DEAL_TIMING_SCHEDULE");
      } else {
        setIsSaveError(true);
      }
    } catch (error) {
      setIsSaveError(true);
      openNotificationWithIcon("error", "UPDATE_DEAL_TIMING_SCHEDULE");
    }
    setSpinning(false);
  }

  const openNotificationWithIcon = (
    type: NotificationType,
    action: keyof typeof ActionForNotification
  ) => {
    NotificationInstanceApi[type]({
      message: popupTitleAndMessage[action][type].title,
      description: popupTitleAndMessage[action][type].description,
      placement: "bottomRight",
    });
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <div>
        {NotificationInstanceContextHolder}
        <Button
          className="tail-mx-auto"
          type="link"
          onClick={showModal}
          size="small"
          icon={<EditOutlined />}
          iconPosition="end"
        >
          Edit
        </Button>
        <Modal
          onCancel={handleClose}
          afterClose={props.afterClose}
          loading={loading}
          maskClosable={false}
          title={
            <h3 className="tail-text-blue-400">Class Timing And Schedule: :</h3>
          }
          open={isModalVisible}
          footer={[
            <Button
              key="ok"
              type="primary"
              block
              onClick={() => {
                setSpinning(true);
                storeDealTimingSchedule();
              }}
            >
              Done
            </Button>,
          ]}
        >
          <Form
            form={form}
            layout="horizontal"
            initialValues={tutionTimingAndState}
            onValuesChange={handleValuesChange}
          >
            <div className="tail-flex tail-flex-col tail-gap-y-6">
              <div>
                <h4 className="tail-font-semibold tail-mb-1">
                  Select no class/week :
                </h4>
                <Form.Item className="tail-mb-0" name="how_many_days_in_a_week">
                  <Radio.Group
                    value={classesPerWeek}
                    onChange={(e) => setClassesPerWeek(e.target.value)}
                    buttonStyle="solid"
                    className="tail-flex tail-justify-between"
                  >
                    {DAYS_PER_WEEK_MASTER.map((value, index) => (
                      <Radio.Button
                        key={value}
                        value={value}
                        className="tail-px-4 tail-py-1 !tail-h-auto tail"
                      >
                        {index + 1}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </div>
              <div>
                <h4 className="tail-font-semibold tail-mb-1">
                  Select timing between:
                </h4>
                <div className="tail-flex">
                  <div className="tail-w-1/2">
                    <Form.Item
                      label="From:"
                      name="tuition_start_time"
                      className="tail-mb-0 tail-ring-1 tail-ring-neutral-300 tail-px-2 tail-py-2 tail-w-fit tail-rounded-md"
                    >
                      <TimePicker
                        needConfirm={false}
                        value={startTime}
                        variant="filled"
                        format={"hh:mm A"}
                        placeholder="hh:mm AM"
                        use12Hours
                        onChange={(e: dayjs.Dayjs, t: string | string[]) =>
                          setStartTime(e)
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className="tail-w-1/2">
                    <Form.Item
                      label="To:"
                      name="tuition_end_time"
                      className="tail-mb-0 tail-ring-1 tail-ring-neutral-300 tail-px-2 tail-py-2 tail-w-fit tail-rounded-md"
                    >
                      <TimePicker
                        needConfirm={false}
                        value={endTime}
                        variant="filled"
                        format={"hh:mm A"}
                        placeholder="hh:mm AM"
                        use12Hours
                        onChange={(e: dayjs.Dayjs, t: string | string[]) =>
                          setEndTime(e)
                        }
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <div className="tail-flex tail-items-center tail-gap-x-2">
                <h4 className="tail-font-semibold">Number of Hours/Day:</h4>
                <Form.Item className="tail-mb-0" name="tuition_hours">
                  <Input
                    type="number"
                    value={hoursPerWeek}
                    onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                    className="tail-w-16 "
                  />
                </Form.Item>
              </div>
              <div className="tail-flex tail-items-center tail-gap-x-2">
                <Form.Item
                  label="Add Internal Note :"
                  name="note"
                  className="tail-w-full"
                >
                  <Input
                    className="tail-w-full"
                    placeholder=""
                    value={addNote}
                    onChange={(e) => setAddNote(e.target.value)}
                  />
                </Form.Item>
              </div>
              {isSaveError && (
                <Alert
                  message="Something went wrong, please try again"
                  type="error"
                  showIcon
                  closable
                  onClose={() => setIsSaveError(false)}
                />
              )}
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
}

export default EditTimeAndSchedule;
